import React, { useEffect, useState, useRef } from "react";
import { useResponsive } from "react-hooks-responsive";
import { useSelector, useDispatch } from "react-redux";
import DayPicker from "react-day-picker";
import { Grid, Row, Col, Modal, Badge, Pager } from "react-bootstrap";
import Select from "react-select";
import moment from "moment-timezone";
import ReactTable from "react-table";
import Card from "../components/Card/Card.jsx";
import Button from "../components/CustomButton/CustomButton.jsx";
import { constants } from "../redux/schedule/actions";
import { commonConstants } from "../redux/common/actions";
import { getFormattedUser } from "../services/auth";
import "react-day-picker/lib/style.css";
import NotificationSystem from "react-notification-system";
import { style as notificationStyling } from "../variables/Variables.jsx";
import ClipLoader from "react-spinners/ClipLoader";
import CustomReactTablePagination from "./Components/CustomReactTablePagination";
import {
  shiftWindowToInstants,
  shiftStartsOn,
  instantToReadableShiftTime,
} from "../utils/timezone";
import { shiftsToDayBookingState } from "../utils/schedule";

const {
  GET_SCHEDULE,
  CREATE_SCHEDULE_ITEM,
  CREATE_SHIFT_ASSIGNMENT,
  REMOVE_SCHEDULE_ITEM,
} = constants;
const { REQUEST } = commonConstants;
const bookedButtonStyle = { whiteSpace: "normal", fontWeight: "bold" };

// Shift definitions in Copline time (America/New_York), fed to
// shiftWindowToInstants. Post-Feb-2022: three 8h shifts. Pre-Feb-2022: two 12h
// shifts, kept only for rendering historical bookings — new bookings are always
// post-Feb-2022.
const POST_FEB_2022_SHIFTS = [
  { start: "00:00", end: "08:00", nextDay: false },
  { start: "08:00", end: "16:00", nextDay: false },
  { start: "16:00", end: "00:00", nextDay: true },
];
const PRE_FEB_2022_SHIFTS = [
  { start: "11:00", end: "23:00", nextDay: false },
  { start: "23:00", end: "11:00", nextDay: true },
];

const Schedule = () => {
  const dispatch = useDispatch();
  const notificationSystem = useRef();
  const [_notificationSystem, setNotificationSystem] = useState({});

  const handleFetchShiftAssignments = (date) => {
    const from = moment(date)
      .startOf("month")
      .subtract(7, "days")
      .format("YYYY-MM-DD");
    const to = moment(date)
      .endOf("month")
      .add(7, "days")
      .format("YYYY-MM-DD");
    dispatch({ type: GET_SCHEDULE + REQUEST, payload: { from, to } });
  };

  // initial page fetch
  useEffect(() => {
    setSelectedDays(getWeekDays(getWeekRange(moment()).from));
    setNotificationSystem(notificationSystem);
    handleFetchShiftAssignments(moment());
  }, [dispatch]);
  // retrieve admin privilege/user if applicable
  const [hasAdmin, setHasAdmin] = useState(false);
  const [selectedScheduleUser, setSelectedScheduleUser] = useState(null);
  useEffect(() => {
    const getAuth = async () => {
      const user = await getFormattedUser();
      // SuperAdmins and HrAdmins have schedule capabilities
      const hasAdmin =
        user &&
        user.roles &&
        (user.roles.includes("SuperAdmin") || user.roles.includes("HrAdmin"));
      setHasAdmin(hasAdmin);
      setSelectedScheduleUser(user.id);
    };
    getAuth().then();
  }, []);
  // handle ajax side effects
  const {
    scheduleTimes = [],
    callTakers = [],
    loading,
    success,
    error,
  } = useSelector((state) => state.schedule);

  // const goToTop = () => {
  //   return document.getElementById('top-of-the-form-to-ya').scrollIntoView()
  // }

  useEffect(() => {
    const notificationSystem = _notificationSystem.current;
    const shiftSuccessfullyAssigned =
      success.source === CREATE_SHIFT_ASSIGNMENT ||
      success.source === CREATE_SCHEDULE_ITEM;
    const shiftAssignmentFailed =
      error.source === CREATE_SHIFT_ASSIGNMENT ||
      error.source === CREATE_SCHEDULE_ITEM;

    if (shiftSuccessfullyAssigned) {
      setCreateScheduleModal({ isShowing: false, payload: {} });
    }
    if (shiftAssignmentFailed) {
      setCreateScheduleModal({ isShowing: false, payload: {} });
    }
    if (success.message) {
      notificationSystem.addNotification({
        message: success.message,
        level: "info",
        position: "tc",
      });
    }
    if (error.message) {
      notificationSystem.addNotification({
        message: error.message,
        level: "error",
        position: "tc",
      });
    }
    return () => {};
  }, [success, error, _notificationSystem]);

  // after go to today click we need to set selected days correctly
  const [todayReset, setTodayReset] = useState(false);
  useEffect(() => {
    if (todayReset) {
      setSelectedDays(getWeekDays(getWeekRange(moment()._d).from));
      setTodayReset(false);
    }
    return () => {};
  }, [todayReset]);

  useEffect(() => {
    // THIS IS SCREWY - Desktop works fine, Mobile scrolls to top quickly then back to original position - 2x fixed this
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    return () => {};
  }, [success.message, error.message]);

  const [createScheduleModal, setCreateScheduleModal] = useState({
    isShowing: false,
    payload: {},
  });

  const [userCard, setUserCard] = useState({ isShowing: false, payload: {} });
  const [hoverRange] = useState(undefined);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [selectedShiftNumber, setSelectedShiftNumber] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment()._d);
  // const [refreshMonth, setRefreshMonth] = useState(moment().month())

  // NOTE: was breaking the week updating functionality so reverted to in method update
  // const updatedSchedule = useMemo(() => {
  //   const list = []
  //   const formattedScheduleTimes = scheduleTimes.map(time => {
  //     return {
  //       ...time,
  //       shiftNumber: time.shift_number,
  //       effectiveDate: moment(time.effective_date),
  //       expiryDate: moment(time.expiry_date)
  //     }
  //   })
  //   selectedDays.forEach(day => {
  //     const shiftsBookedOnDay = formattedScheduleTimes.filter(t => moment(day).isSame(t.effectiveDate, 'day'))
  //     let shiftBookings = []
  //     shiftBookings.push([...shiftsBookedOnDay.filter(shift => shift.shiftNumber === 1)])
  //     shiftBookings.push([...shiftsBookedOnDay.filter(shift => shift.shiftNumber === 2)])
  //     return list.push({ shiftBookings, day })
  //   })
  //   return list
  // }, [scheduleTimes])

  // if (success.source === CREATE_SCHEDULE_ITEM) {
  //   console.log('FAILL IN HERE: ')
  //   document.getElementById('top-of-the-form-to-ya').scrollIntoView()
  // }

  const breakpoints = { xs: 0, sm: 425, md: 1024 };
  const responsiveDetails = useResponsive(breakpoints);
  // const shifts = [
  //   { shiftNumber: '1 (11:00 - 23:00) EST', dates: [], bookingState: [] },
  //   { shiftNumber: '2 (23:00 - 11:00) EST', dates: [], bookingState: [] },
  // ]

  var feb2022 = new Date(2022, 1, 1);

  const handleAssignShift = () => {
    dispatch({
      type: CREATE_SHIFT_ASSIGNMENT + REQUEST,
      payload: {
        shiftNumber: selectedShiftNumber,
        // The instants were already built in Copline time in renderWeek and
        // carried through the modal payload; send them verbatim.
        effectiveDatetime: createScheduleModal.payload.effectiveDate,
        expiryDatetime: createScheduleModal.payload.expiryDate,
        callTakerId:
          (selectedScheduleUser && selectedScheduleUser.value) ||
          selectedScheduleUser,
        bookingType: createScheduleModal.payload.bookingType,
      },
    });
  };

  const handleDayClick = (date) => {
    setSelectedMonth(date);
    setSelectedDays(getWeekDays(getWeekRange(date).from));
  };

  const handleWeekChange = (date, currentPagerDate) => {
    const dateMonth = moment(date).month();
    const currentPagerMonth = moment(currentPagerDate).month();
    const pickerMonth = moment(selectedMonth).month();
    // console.log(dateMonth, currentPagerMonth, pickerMonth)

    if (dateMonth !== currentPagerMonth || pickerMonth !== currentPagerMonth) {
      let newDate = date._d ? date._d : date;
      setSelectedMonth(newDate);
      handleFetchShiftAssignments(newDate);
    }
    setSelectedDays(getWeekDays(getWeekRange(date).from));
  };

  // const handleDayEnter = date => {
  //   setHoverRange(getWeekRange(date))
  // }

  // const handleDayLeave = () => {
  //   setHoverRange(undefined)
  // }

  const handleWeekClick = (weekNumber, days, e) => {
    setSelectedDays(days);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
    handleFetchShiftAssignments(date);
    setSelectedDays(getWeekDays(getWeekRange(date).from));
  };

  const handleTodayButtonClick = () => {
    setTodayReset(true);
  };

  const formatDay = (day) => {
    return moment(day).format("dddd MMM D");
  };

  const renderShift = (user, shift, booking) => {
    if (user) {
      return (
        <Button
          id={`shift-${shift.number}-user`}
          name={`shift${shift.number}User`}
          block
          bsSize={"xsmall"}
          style={{ border: "1px solid black", background: "white" }}
          onClick={() => {
            setUserCard({
              isShowing: true,
              payload: { ...user, hasAdmin },
            });
            setSelectedScheduleId(booking.id);
            return hasAdmin && setSelectedScheduleUser(user.id);
          }}
        >
          <span style={bookedButtonStyle}>{user && user.name}</span>
        </Button>
      );
    } else {
      return (
        <Button
          id={`shift-${shift.number}-action`}
          name={`shift${shift.number}Action`}
          block
          bsSize={"xsmall"}
          style={{
            border: "1px solid black",
            background: shift.type === "VOICE" ? "white" : "yellow",
          }}
          onClick={() => {
            setCreateScheduleModal({
              isShowing: true,
              payload: {
                effectiveDate: shift.effectiveDate,
                expiryDate: shift.expiryDate,
                shiftNumber: shift.number,
                bookingType: shift.type,
              },
            });
            setSelectedShiftNumber(shift.number);
          }}
        >
          <b style={{ color: "red" }}>
            {shift.type === "VOICE" ? "CallTaker-Open" : "TextTaker-Open"}
          </b>
        </Button>
      );
    }
  };

  const renderWeek = () => {
    const { size, screenIsAtLeast } = responsiveDetails;
    // const days = []

    const formattedScheduleTimes = scheduleTimes.map((time) => {
      return {
        ...time,
        effectiveDate: moment(time.effectiveDate),
        expiryDate: moment(time.expiryDate),
      };
    });

    const updatedSchedule = [];
    var hasFeb2022 = false;
    selectedDays.forEach((day) => {
      // Bucket by the shift's Copline calendar date so US and non-US viewers
      // place the same booking in the same column (fixes the old local-vs-EST
      // split). The column key is the cell's plain calendar date.
      const dayKey = moment(day).format("YYYY-MM-DD");
      const shiftsBookedOnDay = formattedScheduleTimes.filter((t) =>
        shiftStartsOn(t, dayKey)
      );
      let shiftBookings = [];

      shiftBookings.push([
        ...shiftsBookedOnDay.filter((shift) => shift.shiftNumber === 1),
      ]);
      shiftBookings.push([
        ...shiftsBookedOnDay.filter((shift) => shift.shiftNumber === 2),
      ]);

      if (day >= feb2022) {
        hasFeb2022 = true;
        shiftBookings.push([
          ...shiftsBookedOnDay.filter((shift) => shift.shiftNumber === 3),
        ]);
      }

      return updatedSchedule.push({ shiftBookings, day });
    });

    const shifts = [];

    if (!hasFeb2022) {
      shifts.push({
        shiftNumber: "1 (11:00 - 23:00) EST",
        dates: [],
        bookingState: [],
      });
      shifts.push({
        shiftNumber: "2 (23:00 - 11:00) EST",
        dates: [],
        bookingState: [],
      });
    } else {
      shifts.push({
        shiftNumber: "1 (00:00 - 08:00) EST",
        dates: [],
        bookingState: [],
      });
      shifts.push({
        shiftNumber: "2 (08:00 - 16:00) EST",
        dates: [],
        bookingState: [],
      });
      shifts.push({
        shiftNumber: "3 (16:00 - 24:00) EST",
        dates: [],
        bookingState: [],
      });
    }

    if (updatedSchedule.length === 0) {
      return;
    }

    const daysOfTheWeek = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    updatedSchedule.forEach((scheduledDay, idx) => {
      const dayOfTheWeek = daysOfTheWeek[idx];
      const { day } = scheduledDay;
      scheduledDay.shiftBookings.forEach((booking, i) => {
        const shiftNumber = i + 1;

        const columnDateStr = moment(day).format("YYYY-MM-DD");
        const shiftConfigs = hasFeb2022
          ? POST_FEB_2022_SHIFTS
          : PRE_FEB_2022_SHIFTS;
        const { effectiveDatetime: effectiveDate, expiryDatetime: expiryDate } =
          shiftWindowToInstants(columnDateStr, shiftConfigs[i]);

        const beforeDec2020 = moment(effectiveDate).isBefore(
          moment("2020-12-01")
        );
        const voiceShifts = booking.filter((b) => b.bookingType === "VOICE");

        const shiftUserOne = voiceShifts[0] && callTakers.find(u => u.id === voiceShifts[0].callTakerId);
        const shiftUserTwo = voiceShifts[1] && callTakers.find(u => u.id === voiceShifts[1].callTakerId);

        var shift = (
          <>
            {renderShift(
              shiftUserOne,
              {
                number: shiftNumber,
                effectiveDate: effectiveDate,
                expiryDate: expiryDate,
                type: "VOICE",
              },
              voiceShifts[0]
            )}
            {renderShift(
              shiftUserTwo,
              {
                number: shiftNumber,
                effectiveDate: effectiveDate,
                expiryDate: expiryDate,
                type: "VOICE",
              },
              voiceShifts[1]
            )}
            {voiceShifts.length > 2 && voiceShifts.slice(2).map((voiceShift, index) => {
              const additionalUser = callTakers.find(u => u.id === voiceShift.callTakerId);
              return (
                <div style={{ marginTop: "4px" }} key={`additional-${voiceShift.id || index}`}>
                  {renderShift(
                    additionalUser,
                    {
                      number: shiftNumber,
                      effectiveDate: effectiveDate,
                      expiryDate: expiryDate,
                      type: "VOICE",
                    },
                    voiceShift
                  )}
                </div>
              );
            })}
            {hasAdmin && (
              <div style={{ marginTop: "4px" }}>
                <Button
                  block
                  bsSize={"xsmall"}
                  style={{
                    border: "1px solid black",
                    background: "#06B6D4",
                    color: "#111827"
                  }}
                  onClick={() => {
                    setCreateScheduleModal({
                      isShowing: true,
                      payload: {
                        effectiveDate: effectiveDate,
                        expiryDate: expiryDate,
                        shiftNumber: shiftNumber,
                        bookingType: "VOICE",
                      },
                    });
                    setSelectedShiftNumber(shiftNumber);
                  }}
                >
                  <b>+ Add Volunteer</b>
                </Button>
              </div>
            )}
          </>
        );
        shifts[i][dayOfTheWeek] = shift;
        
        let bookingState;
        if (booking.length === 0) {
          bookingState = "empty";
        } else if (beforeDec2020) {
          bookingState = booking.length >= 3 ? "full" : "partial";
        } else {
          bookingState = booking.length >= 2 ? "full" : "partial";
        }
        
        shifts[i]["bookingState"].push(bookingState);
        shifts[i]["dates"].push(day);
      });
    });

    // Mobile View Render
    if (size === "xs") {
      if (loading.source === GET_SCHEDULE) {
        return (
          <div className="main-content" style={{ textAlign: "center" }}>
            <ClipLoader loading size={50} color="grey" />
          </div>
        );
      }
      var mobileDayStack = [];
      const flagColor = {
        empty: "red",
        partial: "dodgerblue",
        full: "green",
      };
      daysOfTheWeek.forEach((d, index) => {
        const daydate = moment(shifts[0]["dates"][index]);
        const isFeb2022 = daydate >= new Date(2022, 1, 1);
        mobileDayStack.push(
          <React.Fragment key={d}>
            <Row style={{ marginTop: "15px" }}>
              <Col xs={3}> </Col>
              <Col xs={9}>
                <b>{moment(shifts[0]["dates"][index]).format("dddd MMM D")}</b>
              </Col>
            </Row>
            <Row style={{ marginBottom: "15px" }}>
              <Col xs={3}>
                <Badge
                  style={{
                    backgroundColor:
                      flagColor[shifts[0]["bookingState"][index]],
                  }}
                >
                  Shift 1
                </Badge>
                <p style={{ fontSize: "10px" }}>
                  {isFeb2022 ? "(00:00-08:00) EST" : "(11:00-23:00) EST"}
                </p>
              </Col>
              <Col xs={9}>{shifts[0][d]}</Col>
            </Row>
            <Row>
              <Col xs={3}>
                <Badge
                  style={{
                    backgroundColor:
                      flagColor[shifts[1]["bookingState"][index]],
                  }}
                >
                  Shift 2
                </Badge>
                <p style={{ fontSize: "10px" }}>
                  {isFeb2022 ? "(08:00-16:00) EST" : "(23:00-11:00) EST"}
                </p>
              </Col>
              <Col xs={9}>{shifts[1][d]}</Col>
            </Row>
            {isFeb2022 && (
              <Row>
                <Col xs={3}>
                  <Badge
                    style={{
                      backgroundColor:
                        flagColor[shifts[2]["bookingState"][index]],
                    }}
                  >
                    Shift 3
                  </Badge>
                  <p style={{ fontSize: "10px" }}>(16:00-24:00) EST</p>
                </Col>
                <Col xs={9}>{shifts[2][d]}</Col>
              </Row>
            )}
          </React.Fragment>
        );
      });
      return mobileDayStack;
    }

    const headerStyle = {
      textAlign: "center",
      fontWeight: "bold",
      color: "#333333",
    };

    // Desktop View Render
    if (screenIsAtLeast("sm")) {
      return (
        <>
          <ReactTable
            PaginationComponent={CustomReactTablePagination}
            showTotalPages={true}
            canNextFromData={false}
            data={shifts}
            columns={[
              {
                Header: "Shift #",
                accessor: "shiftNumber",
                width: 150,
                style: { textAlign: "center" },
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[0]),
                accessor: "sunday",
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[1]),
                accessor: "monday",
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[2]),
                accessor: "tuesday",
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[3]),
                accessor: "wednesday",
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[4]),
                accessor: "thursday",
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[5]),
                accessor: "friday",
                headerStyle: headerStyle,
              },
              {
                Header: formatDay(selectedDays[6]),
                accessor: "saturday",
                headerStyle: headerStyle,
              },
              { accessor: "bookingState", show: false },
            ]}
            getTdProps={(state, rowInfo, column) => {
              const normalizedDayString = `${column.Header[0].toLowerCase()}${column.Header.slice(
                1
              )}`;
              const cellStateIndex = daysOfTheWeek.findIndex((day) =>
                normalizedDayString.includes(day)
              );
              const cellState =
                rowInfo &&
                rowInfo.original &&
                rowInfo.original.bookingState[cellStateIndex];
              const borderMappingColours = {
                full: "green",
                partial: "dodgerblue",
                empty: "red",
              };
              return {
                style: {
                  margin: "1px",
                  ...(cellState === "empty" && {
                    border: `2px solid ${borderMappingColours[cellState]}`,
                  }),
                  ...(["full", "partial"].includes(cellState) && {
                    background: borderMappingColours[cellState],
                  }),
                },
              };
            }}
            showPaginationBottom={false}
            sortable={false}
            loading={loading.source === GET_SCHEDULE}
          />
        </>
      );
    }
  };

  const getWeekDays = (weekStart) => {
    const days = [weekStart];
    for (let i = 1; i < 7; i += 1) {
      days.push(moment(weekStart).add(i, "days").toDate());
    }
    return days;
  };

  const getWeekRange = (date) => {
    return {
      from: moment(date).startOf("week").toDate(),
      to: moment(date).endOf("week").toDate(),
    };
  };

  const renderCalendarDay = (day) => {
    const dayKey = moment(day).format("YYYY-MM-DD");
    const dayColors = { empty: "red", partial: "dodgerblue", full: "green" };
    const color = dayColors[shiftsToDayBookingState(scheduleTimes, dayKey)];
    return <div style={{ color }}>{moment(day).date()}</div>;
  };

  const daysAreSelected = selectedDays.length > 0;
  const modifiers = {
    hoverRange,
    selectedRange: daysAreSelected && {
      from: selectedDays[0],
      to: selectedDays[6],
    },
    hoverRangeStart: hoverRange && hoverRange.from,
    hoverRangeEnd: hoverRange && hoverRange.to,
    selectedRangeStart: daysAreSelected && selectedDays[0],
    selectedRangeEnd: daysAreSelected && selectedDays[6],
    selectedDays,
  };
  const currentPagerDate = moment(selectedDays[0]);
  const weekNavigatorString = moment(selectedDays[0]).format("MMMM Do");
  const previousWeekDate = moment(selectedDays[0]).subtract(1, "w");
  const nextWeekDate = moment(selectedDays[0]).add(1, "w");

  // if (loading.source === GET_SCHEDULE) {
  //   return (
  //     <div className='main-content' style={{ textAlign: 'center' }}>
  //       <ClipLoader loading size={50} color="grey" />
  //     </div>
  //   )
  // }

  return (
    <div className="main-content">
      <NotificationSystem
        ref={notificationSystem}
        style={notificationStyling}
      />
      <Grid fluid>
        <Card
          content={
            <>
              <Row>
                <Col xs={12} sm={4}>
                  <DayPicker
                    id={"daypicker"}
                    month={selectedMonth}
                    selectedDays={selectedDays}
                    showOutsideDays
                    modifiers={modifiers}
                    modifiersStyles={{
                      selectedDays: {
                        color: "black",
                        background: "transparent",
                      },
                    }}
                    renderDay={renderCalendarDay}
                    onDayClick={handleDayClick}
                    // onDayMouseEnter={handleDayEnter}
                    // onDayMouseLeave={handleDayLeave}
                    onWeekClick={handleWeekClick}
                    onMonthChange={handleMonthChange}
                    todayButton="Back to Today"
                    onTodayButtonClick={handleTodayButtonClick}
                  />
                </Col>
                {daysAreSelected && (
                  <>
                    <Col xs={12} sm={6}>
                      <Pager>
                        <Pager.Item
                          onClick={() =>
                            handleWeekChange(previousWeekDate, currentPagerDate)
                          }
                        >
                          {"<"}
                        </Pager.Item>
                        {`  Week of ${weekNavigatorString}  `}
                        <Pager.Item
                          onClick={() =>
                            handleWeekChange(nextWeekDate, currentPagerDate)
                          }
                        >
                          {">"}
                        </Pager.Item>
                      </Pager>
                      {/*<Col xs={12} sm={12} style={{ textAlign: 'center' }}>*/}
                      {/*  <h5>Coverage Stats</h5>*/}
                      {/*  <p style={{ fontStyle: 'italic', fontSize: '13px', marginTop: '-16px' }}>(of total shifts available per selected week)</p>*/}
                      {/*</Col>*/}
                      {/*{hasAdmin &&*/}
                      {/*  <Col xs={12} sm={12}>*/}
                      {/*    <ChartistGraph*/}
                      {/*      data={{*/}
                      {/*        labels: ['Filled', 'Partial', 'Empty'],*/}
                      {/*        series: [20, 30, 50]*/}
                      {/*      }}*/}
                      {/*      type={'Pie'}*/}
                      {/*      options={{*/}
                      {/*        height: '150px', width: '135px'*/}
                      {/*      }}*/}
                      {/*      className={'ct-octave custom-pie'}*/}
                      {/*      style={{ marginTop: '-15px' }}*/}
                      {/*    />*/}
                      {/*  </Col>*/}
                      {/*}*/}
                    </Col>
                  </>
                )}
              </Row>
              {renderWeek()}
            </>
          }
        />
      </Grid>
      <Modal
        show={createScheduleModal.isShowing}
        onHide={() => setCreateScheduleModal({ isShowing: false, payload: {} })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Booking</Modal.Title>
          <Modal.Body>
            {loading.source === CREATE_SCHEDULE_ITEM && (
              <div style={{ textAlign: "center" }}>
                <ClipLoader loading size={50} color="grey" />
              </div>
            )}
            {!loading.source && (
              <>
                <p>
                  {hasAdmin
                    ? "Who would you like to schedule for this shift?"
                    : "Are you sure you want to book this shift?"}
                </p>
                <p>
                  {instantToReadableShiftTime(
                    createScheduleModal.payload.effectiveDate
                  )}
                  {" => "}
                  {instantToReadableShiftTime(
                    createScheduleModal.payload.expiryDate
                  )}
                </p>
                {!hasAdmin && <b>You cannot reverse this decision!</b>}
                {hasAdmin && (
                  <>
                    <p>Call Taker List:</p>
                    <Select
                      options={callTakers
                        .filter((ct) => ct.availableForScheduling)
                        .map((user) => {
                          return { label: user.name, value: user.id };
                        })}
                      value={selectedScheduleUser}
                      isSearchable={true}
                      name="scheduledUserList"
                      onChange={(event) => setSelectedScheduleUser(event)}
                    />
                  </>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              bsStyle={"primary"}
              fill
              block
              disabled={loading.source === CREATE_SCHEDULE_ITEM}
              onClick={handleAssignShift}
            >
              Yes
            </Button>
            <Button
              block
              disabled={loading.source === CREATE_SCHEDULE_ITEM}
              onClick={() =>
                setCreateScheduleModal({ isShowing: false, payload: {} })
              }
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal.Header>
      </Modal>
      <Modal
        show={userCard.isShowing}
        onHide={() =>
          setUserCard({ isShowing: false, payload: { ...userCard.payload } })
        }
      >
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
          <Modal.Body>
            <Row>
              <Col xs={3}>Name: </Col>
              <Col
                xs={9}
              >{`${userCard.payload.firstName} ${userCard.payload.lastName}`}</Col>
            </Row>
            <Row>
              <Col xs={3}>Email: </Col>
              <Col xs={9}>{userCard.payload.email}</Col>
            </Row>
            <Row>
              <Col xs={3}>Phone: </Col>
              <Col xs={9}>{userCard.payload.cellPhone?.national || ''}</Col>
            </Row>
            <Row>
              <Col xs={3}>
                {userCard.payload.country === "CANADA" ? "Province" : "State"}:{" "}
              </Col>
              <Col xs={9}>{userCard.payload.location}</Col>
            </Row>
            {hasAdmin && (
              <Row style={{ marginTop: "20px" }}>
                <Col>
                  <Button
                    bsStyle={"primary"}
                    fill
                    block
                    onClick={() => {
                      dispatch({
                        type: REMOVE_SCHEDULE_ITEM + REQUEST,
                        payload: {
                          id: selectedScheduleId,
                          callTakerId:
                            (selectedScheduleUser &&
                              selectedScheduleUser.value) ||
                            selectedScheduleUser,
                          date: selectedMonth,
                        },
                      });
                      setUserCard({ isShowing: false, payload: {} });
                    }}
                  >
                    Remove User from Shift
                  </Button>
                </Col>
              </Row>
            )}
          </Modal.Body>
        </Modal.Header>
      </Modal>
    </div>
  );
};

export default Schedule;
